'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { getTerraGraphSeriesColor, token } from '@terra/ui-styled-system';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface RadialBarStrokedGaugeProps {
  /**
   * The value to display in the gauge (0-100)
   */
  value: number;
  /**
   * Optional label text shown below the gauge
   */
  label?: string;
  /**
   * Height of the chart
   */
  height?: number | string;
  /**
   * Width of the chart
   */
  width?: number | string;
  /**
   * Array of colors for the gauge
   */
  colors?: string[];
  /**
   * Custom formatter for the displayed value
   */
  valueFormatter?: (value: number) => string;
}

export function RadialBarStrokedGauge({
  value,
  label = '',
  height = 350,
  width = '100%',
  colors = [getTerraGraphSeriesColor()],
  valueFormatter = (val) => `${val}%`,
}: RadialBarStrokedGaugeProps) {
  // Validate value is between 0-100
  const validatedValue = Math.min(Math.max(value, 0), 100);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'radialBar',
        offsetY: -10,
        foreColor: 'currentColor',
        animations: {
          enabled: true,
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: {
            margin: 0,
            size: '70%',
          },
          track: {
            background: token('colors.bg.subtle'),
            strokeWidth: '97%',
            margin: 5,
            dropShadow: {
              enabled: false,
            },
          },
          dataLabels: {
            name: {
              fontSize: '14px',
              color: undefined,
              offsetY: 120,
              show: true,
            },
            value: {
              offsetY: 50,
              fontSize: '14px',
              color: token('colors.fg.default'),
              formatter: function (val) {
                return valueFormatter(val);
              },
              show: true,
            },
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.15,
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 50, 65, 91],
        },
      },
      stroke: {
        dashArray: 4,
        lineCap: 'round',
      },
      colors: colors,
      labels: [label],
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300,
            },
          },
        },
      ],
    }),
    [colors, label, valueFormatter],
  );

  return (
    <ReactApexChart
      options={options}
      series={[validatedValue]}
      type="radialBar"
      height={height}
      width={width}
    />
  );
}

export default RadialBarStrokedGauge;
